import { z } from "zod";
import { getDisplayNameByKey, orderableItemValues } from "@sellar-io/common";
import {
  marketContainerTypes,
  dietaryTypes,
  dietaryTypeValues,
} from "./marketSearchFilterOptions";
import { filterStyles } from "./marketSearchFilters";

const categoryKey = z.enum([
  "beer",
  "cider",
  "spirits",
  "softDrinks",
  "wine",
  "mead",
]);
const label = z.string().min(1);
const scope = z.enum(["market", "suppliers"]).optional();
export const marketSearchSelectionSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("supplier"),
    supplierId: z.number().int().positive(),
    location: z.string().nullable().optional(),
    logoUrl: z.string().nullable().optional(),
    label,
  }),
  z.object({
    kind: z.literal("product"),
    parentProductId: z.number().int().positive(),
    productItemId: z.number().int().positive(),
    imageUrl: z.string().nullable().optional(),
    supplierId: z.number().int().positive(),
    supplierName: label,
    label,
  }),
  z.object({
    kind: z.literal("category"),
    categoryKey,
    value: z.string(),
    label,
  }),
  z.object({
    kind: z.literal("container"),
    value: z.enum(marketContainerTypes),
    label,
  }),
  z.object({
    kind: z.literal("dietary"),
    value: z.enum(dietaryTypeValues),
    label,
  }),
  z.object({ kind: z.literal("style"), categoryKey, value: z.string(), label }),
  z.object({
    kind: z.literal("filterQuery"),
    categoryKey,
    styleSearchValues: z.string().array(),
    containerType: z.enum(marketContainerTypes),
    label,
  }),
  z.object({
    kind: z.literal("filters"),
    preset: z.enum(["neipaKeg", "imperialStoutCans"]),
    label,
  }),
  z.object({
    kind: z.literal("text"),
    scope,
    value: z.string().trim().min(1).max(100),
    label,
  }),
]);
export type MarketSearchSelection = z.infer<typeof marketSearchSelectionSchema>;

export type SearchOption = MarketSearchSelection;

export const marketSearchHistorySchema = z
  .array(marketSearchSelectionSchema)
  .max(6);
export const selectionKey = (selection: MarketSearchSelection) => {
  switch (selection.kind) {
    case "supplier":
      return `supplier:${selection.supplierId}`;
    case "product":
      return `product:${selection.parentProductId}`;
    case "category":
      return `category:${selection.categoryKey}`;
    case "style":
      return `style:${selection.categoryKey}:${selection.value}`;
    case "container":
    case "dietary":
      return `${selection.kind}:${selection.value}`;
    case "filters":
      return `filters:${selection.preset}`;
    case "filterQuery":
      return `filterQuery:${selection.categoryKey}:${selection.styleSearchValues.join(",")}:${selection.containerType}`;
    case "text":
      return `text:${selection.scope ?? "market"}:${selection.value.toLocaleLowerCase()}`;
  }
};

const filterSuggestions: MarketSearchSelection[] = Object.values(
  filterStyles,
).flatMap((category): MarketSearchSelection[] => [
  {
    kind: "category",
    categoryKey: category.key,
    value: category.searchValue,
    label: category.displayValue,
  },
  ...("children" in category
    ? category.children.map((style): MarketSearchSelection => ({
        kind: "style",
        categoryKey: category.key,
        value: ["beer", "spirits", "softDrinks"].includes(category.key)
          ? style.searchValue
          : [style.searchValue, category.searchValue].join(" & "),
        label: style.displayValue,
      }))
    : []),
]);

export const localMarketSuggestions: MarketSearchSelection[] = [
  ...filterSuggestions,
  ...marketContainerTypes.map((value): MarketSearchSelection => ({
    kind: "container",
    value,
    label:
      getDisplayNameByKey(orderableItemValues, value) ??
      value
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/^./, (character) => character.toUpperCase()),
  })),
  ...dietaryTypeValues.map((value): MarketSearchSelection => ({
    kind: "dietary",
    value,
    label: dietaryTypes[value],
  })),
];

export const marketCategoryShortcuts: MarketSearchSelection[] = (
  ["beer", "cider", "wine", "spirits", "softDrinks"] as const
).map((key): MarketSearchSelection => ({
  kind: "category",
  categoryKey: key,
  value: filterStyles[key].searchValue,
  label: key === "softDrinks" ? "Softs" : filterStyles[key].displayValue,
}));
